import {
  asWalletCurrency,
  formatWalletAmount,
} from 'src/pages/Wallet/Wallet.money';
import { OWNER_REQUIRED_REFUSAL_CODES } from '../../../../SubscriptionManager.constants';
import type {
  tRetentionDiscountOffer,
  tRetentionFlowError,
} from '../../../../SubscriptionManager.types';

/** One minor unit (cent) in the wallet formatter's micro-units. */
const MICROS_PER_MINOR_UNIT = 10_000n;

export type tRetentionOfferPriceView = {
  currentAmount: string;
  offerAmount: string;
  /** What the customer saves over the whole discount period. */
  totalSavings: string;
};

/**
 * A retention offer amount (integer MINOR units, as leadbilling sends it) in
 * the customer's locale. It goes through the wallet formatter, the one the
 * subscription screens already use, so a price here reads like every other
 * price. Null when that formatter does not render the currency: the caller
 * leaves the amount out rather than printing it in the wrong format.
 */
export function formatRetentionOfferAmount(options: {
  amount: number;
  currency: string;
  locale: string;
}): string | null {
  const currency = asWalletCurrency(options.currency);
  if (!currency || !Number.isSafeInteger(options.amount)) return null;

  return formatWalletAmount({
    amountMicros: (BigInt(options.amount) * MICROS_PER_MINOR_UNIT).toString(),
    currency,
    locale: options.locale,
  });
}

/**
 * The discount's amounts ready to show, or null when any of them cannot be
 * shown exactly. All or nothing: a price row without its pair, or savings
 * without the prices they come from, would be half a claim. The savings are
 * integer arithmetic on the server's own amounts, never a recalculated price.
 */
export function resolveRetentionOfferPriceView(options: {
  offer: tRetentionDiscountOffer;
  locale: string;
}): tRetentionOfferPriceView | null {
  const { offer, locale } = options;
  if (!offer.pricing) return null;

  const { currency, currentAmount, offerAmount } = offer.pricing;
  const format = (amount: number) =>
    formatRetentionOfferAmount({ amount, currency, locale });

  const current = format(currentAmount);
  const discounted = format(offerAmount);
  const savings = format((currentAmount - offerAmount) * offer.durationMonths);

  return current && discounted && savings
    ? { currentAmount: current, offerAmount: discounted, totalSavings: savings }
    : null;
}

export type tRetentionFlowErrorView = {
  messageKey: string;
  /** Back to the offer: only where trying again can still apply it. */
  canRetry: boolean;
  /** Straight to scheduling the cancellation. */
  canContinueToCancellation: boolean;
};

const ERROR_COPY = 'cancelSubscription.retentionOfferError';

/**
 * What the error state says, and which ways out it offers, for a retention
 * step that did not complete. Localized by refusal code; unknown codes get the
 * generic copy for their stage. bff-web's `message` is never shown.
 *
 * The cancellation stays reachable from a failed lookup and from an offer that
 * no longer applies — nothing was offered, so the customer is not kept from
 * cancelling. After a failed accept the way out is a retry: going back to the
 * offer, where declining it schedules the cancellation.
 */
export function resolveRetentionFlowErrorView(
  error: tRetentionFlowError,
): tRetentionFlowErrorView {
  const { stage, code } = error;

  // Cancelling is Owner-only too, so no button that would only fail again.
  if (code && OWNER_REQUIRED_REFUSAL_CODES.includes(code)) {
    return {
      messageKey: `${ERROR_COPY}.ownerRequired`,
      canRetry: false,
      canContinueToCancellation: false,
    };
  }

  if (stage === 'lookup') {
    return {
      messageKey: `${ERROR_COPY}.lookupFailed`,
      canRetry: false,
      canContinueToCancellation: true,
    };
  }

  switch (code) {
    case 'offer_failed':
      // Nothing was applied and the cooldown was not consumed.
      return {
        messageKey: `${ERROR_COPY}.offerFailed`,
        canRetry: true,
        canContinueToCancellation: false,
      };
    case 'offer_ineligible':
      return {
        messageKey: `${ERROR_COPY}.offerIneligible`,
        canRetry: false,
        canContinueToCancellation: true,
      };
    case 'session_not_open':
    case 'reason_required':
      return {
        messageKey: `${ERROR_COPY}.sessionClosed`,
        canRetry: false,
        canContinueToCancellation: false,
      };
    default:
      // No answer, or one this screen does not know: the offer may or may not
      // have applied, so the copy never claims that nothing changed. A retry is
      // safe — the server applies an offer at most once per session.
      return {
        messageKey: `${ERROR_COPY}.acceptUnconfirmed`,
        canRetry: true,
        canContinueToCancellation: false,
      };
  }
}
